import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface Profile {
  id: string;
  user_id: string;
  full_name: string | null;
  npi: string | null;
  license_number: string | null;
  practice_type: string | null;
  profession_id: string | null;
  region_id: string | null;
  organization_id: string | null;
  trial_started_at: string | null;
  subscription_status: string;
  setup_complete: boolean;
  created_at: string;
  updated_at: string;
}

export function useProfile() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user.id)
        .single();
      if (error) throw error;
      return data as Profile;
    },
    enabled: !!user,
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (updates: Partial<Profile>) => {
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase
        .from("profiles")
        .update(updates)
        .eq("user_id", user.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });
}

export function useTrialStatus() {
  const { data: profile } = useProfile();

  if (!profile) return { isTrialActive: false, daysLeft: 0, status: "loading" as const };

  if (profile.subscription_status === "active") {
    return { isTrialActive: false, daysLeft: 0, status: "subscribed" as const };
  }

  // Beta mode: treat all users as active subscribers — no trial expiry enforcement
  return { isTrialActive: false, daysLeft: 0, status: "subscribed" as const };
}
